"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";

type State = { kind: "idle" } | { kind: "submitting" } | { kind: "ok" } | { kind: "error"; message: string };

export function LeadForm({
  firmSlug,
  firmName,
  source,
}: {
  firmSlug?: string;
  firmName?: string;
  source?: string;
}) {
  const [state, setState] = useState<State>({ kind: "idle" });
  // Record when the form first became interactive. Combined with a
  // server-side minimum-submit delay, this catches bot submissions that
  // fill and POST in under a second. We initialize in an effect so the
  // ref stays referentially stable through SSR/hydration (React 19 strict
  // mode flags Date.now() in the render path as impure).
  const startedAt = useRef<number>(0);
  useEffect(() => {
    if (startedAt.current === 0) startedAt.current = Date.now();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setState({ kind: "submitting" });
    const fd = new FormData(e.currentTarget);
    const accountSizeRaw = fd.get("accountSizeUsd");
    const body = {
      email: fd.get("email"),
      firmSlug: firmSlug ?? (fd.get("firmSlug") as string | null) ?? null,
      displayName: (fd.get("displayName") as string | null) || null,
      accountSizeUsd: accountSizeRaw ? Number(accountSizeRaw) : null,
      countryCode: (fd.get("countryCode") as string | null) || null,
      message: (fd.get("message") as string | null) || null,
      source: source ?? "lead-form",
      consentDeltaZContact: fd.get("consentDeltaZContact") === "on",
      consentThirdPartyMatch: fd.get("consentThirdPartyMatch") === "on",
      formStartedAt: startedAt.current,
      // Honeypot: same name as the server expects, hidden from humans via CSS.
      company_website: fd.get("company_website") ?? "",
    };
    try {
      const r = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        setState({ kind: "error", message: j.error ?? "Could not submit." });
        return;
      }
      setState({ kind: "ok" });
    } catch {
      setState({ kind: "error", message: "Network error. Try again." });
    }
  }

  if (state.kind === "ok") {
    return (
      <div className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6">
        <p className="text-sm">
          Got it. We&apos;ll be in touch with matched firms. No spam.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="rounded-[var(--radius)] border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4"
    >
      <div>
        <h3 className="text-lg font-medium">
          {firmName ? `Get matched with ${firmName}` : "Get matched with prop firms"}
        </h3>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Tell us about your trading style and we&apos;ll connect you with firms whose rules fit.
        </p>
      </div>

      {/* Honeypot: invisible, off-screen, no tab focus. Real users never fill this. */}
      <div aria-hidden="true" className="absolute -left-[10000px] -top-[10000px] h-0 w-0 overflow-hidden">
        <label>
          Company website
          <input
            type="text"
            name="company_website"
            tabIndex={-1}
            autoComplete="off"
            defaultValue=""
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">Email *</span>
          <input
            name="email"
            type="email"
            required
            className="w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">Name (optional)</span>
          <input
            name="displayName"
            type="text"
            maxLength={80}
            className="w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">Target account size (USD)</span>
          <input
            name="accountSizeUsd"
            type="number"
            min={0}
            max={10_000_000}
            placeholder="e.g. 100000"
            className="w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="block text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">Country (ISO-2)</span>
          <input
            name="countryCode"
            type="text"
            maxLength={2}
            placeholder="US"
            pattern="[A-Za-z]{2}"
            className="w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3 py-2 uppercase"
          />
        </label>
      </div>
      <label className="block">
        <span className="block text-xs uppercase tracking-wide text-[var(--fg-muted)] mb-1">What are you looking for?</span>
        <textarea
          name="message"
          rows={3}
          maxLength={4000}
          placeholder="Style, instruments, drawdown preference, news trading needs, anything firms should know."
          className="w-full rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg)] px-3 py-2"
        />
      </label>

      <div className="space-y-3 pt-2 border-t border-[var(--border)]">
        <label className="flex items-start gap-3 text-sm text-[var(--fg-muted)]">
          <input
            type="checkbox"
            name="consentDeltaZContact"
            required
            className="mt-1 size-4 rounded border-[var(--border)] bg-[var(--bg)]"
          />
          <span>
            <span className="text-[var(--fg)]">Required.</span> I consent to Delta-X processing this submission to
            match me with prop firms.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-[var(--fg-muted)]">
          <input
            type="checkbox"
            name="consentThirdPartyMatch"
            className="mt-1 size-4 rounded border-[var(--border)] bg-[var(--bg)]"
          />
          <span>
            <span className="text-[var(--fg)]">Optional.</span> I consent to Delta-X sharing my contact details with
            matched prop firms for them to contact me directly. I can withdraw consent at any time.
          </span>
        </label>
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={state.kind === "submitting"}>
          {state.kind === "submitting" ? "Sending..." : "Get matched"}
        </Button>
        {state.kind === "error" ? (
          <span className="text-sm text-amber-400">{state.message}</span>
        ) : null}
      </div>
    </form>
  );
}
